/**
 * Shared axios client for "real" mode.
 *
 * All HTTP service implementations (src/services/<domain>/*.http.ts) go through
 * this single instance so auth-token injection and 401 refresh are handled in
 * one place. In "mock" mode this client is never invoked.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
} from 'axios'
import { AUTH_STORAGE_KEYS } from '@/types/auth.types'
import { getApiGatewayUrl } from '@/config/runtime'

/**
 * Base URL for the API Gateway. Resolved via runtime config (per-deploy
 * ConfigMap) → VITE_API_GATEWAY_URL → relative '/api/v1'. The relative default
 * lets the Vite dev proxy / production nginx handle routing and avoids CORS.
 */
const BASE_URL = getApiGatewayUrl()

const REFRESH_PATH = '/auth/refresh'
const LOGIN_ROUTE = '/login'

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

// ---------------------------------------------------------------------------
// Token helpers (localStorage is the source of truth, shared with AuthContext)
// ---------------------------------------------------------------------------

function getToken(): string | null {
  try {
    return (
      sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) ||
      localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
    )
  } catch {
    return null
  }
}

function getRefreshToken(): string | null {
  try {
    return (
      sessionStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) ||
      localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    )
  } catch {
    return null
  }
}

function setToken(token: string): void {
  try {
    const isRemember = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
    const storage = isRemember ? localStorage : sessionStorage
    storage.setItem(AUTH_STORAGE_KEYS.TOKEN, token)
  } catch {
    /* ignore */
  }
}

function clearAuth(): void {
  try {
    Object.values(AUTH_STORAGE_KEYS).forEach(k => {
      localStorage.removeItem(k)
      sessionStorage.removeItem(k)
    })
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Request interceptor — attach the bearer token
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

// ---------------------------------------------------------------------------
// Response interceptor — single-flight 401 refresh, then retry once
// ---------------------------------------------------------------------------

export type ManualCommandConfig = AxiosRequestConfig & { manualCommand: true }
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean; manualCommand?: boolean }

let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    // Use a bare axios call (not apiClient) to avoid interceptor recursion.
    const res = await axios.post(
      `${BASE_URL}${REFRESH_PATH}`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    const newToken: string | undefined =
      res.data?.access_token ?? res.data?.token ?? res.data?.accessToken
    if (newToken) {
      setToken(newToken)
      window.dispatchEvent(new Event('taxhive:tokens-refreshed'))
      return newToken
    }
    return null
  } catch {
    return null
  }
}

function redirectToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_ROUTE) {
    window.location.assign(LOGIN_ROUTE)
  }
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined
    const status = error.response?.status

    // Only attempt refresh once, and never for the refresh/login calls themselves.
    const isAuthCall = original?.url?.includes('/auth/')

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true

      if (!refreshInFlight) {
        refreshInFlight = refreshAccessToken().finally(() => {
          refreshInFlight = null
        })
      }
      const newToken = await refreshInFlight

      if (newToken) {
        // Renew the session, but require a deliberate retry of human commands.
        // Their original consent/review/operation frame must not be replayed
        // silently under refreshed authority.
        if (original.manualCommand) return Promise.reject(error)
        original.headers.set('Authorization', `Bearer ${newToken}`)
        return apiClient(original)
      }

      clearAuth()
      redirectToLogin()
    }

    return Promise.reject(error)
  }
)

/** Unwrap an axios response to its `.data` payload. */
export async function unwrap<T>(promise: Promise<AxiosResponse<T>>): Promise<T> {
  const res = await promise
  return res.data
}

/**
 * Normalize an axios error into a plain Error with a user-facing message.
 * Handles the gateway's `{ error_code, error_message }` envelope as well as
 * FastAPI's `{ detail }` body.
 */
export function toServiceError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as
      | { detail?: unknown; error_message?: string; message?: string }
      | undefined
    // FastAPI 422 returns detail as an array of validation objects — only use
    // detail when it's a plain string.
    const detailObject =
      body?.detail && typeof body.detail === 'object'
        ? (body.detail as Record<string, unknown>)
        : undefined
    const detail =
      typeof body?.detail === 'string'
        ? body.detail
        : typeof detailObject?.error_message === 'string'
          ? detailObject.error_message
          : typeof detailObject?.message === 'string'
            ? detailObject.message
            : undefined
    const message = body?.error_message ?? detail ?? body?.message
    if (message) return new Error(message)
    if (!error.response) return new Error('Network error — could not reach the server.')
    return new Error(error.message)
  }
  return error instanceof Error ? error : new Error('Unexpected error')
}
